import React from "react"
import { motion } from "framer-motion"
import { FileText, ShieldCheck, GraduationCap, UserCheck, Briefcase, Stethoscope, Scale, Clock, CheckCircle } from "lucide-react"
import Layout from "../../components/common/Layout"
import SEO from "../../components/common/SEO"
import "./ServicePage.css"

const DocumentVerification = () => {
  const docTypes = [
    { icon: <GraduationCap />, title: "Educational", desc: "Degree certificates, transcripts, and mark sheets authentication." },
    { icon: <UserCheck />, title: "Personal", desc: "Birth/marriage certificates and passport copy verification." },
    { icon: <Briefcase />, title: "Professional", desc: "Experience letters and professional license attestation." },
    { icon: <Stethoscope />, title: "Medical", desc: "Medical fitness and vaccination record authentication." },
    { icon: <Scale />, title: "Legal", desc: "Police clearance and legal background check verification." }
  ]

  const attestationLevels = [
    { title: "State Level", desc: "Attestation by state education and home departments." },
    { title: "Central Level", desc: "Verification by MEA and central government ministries." },
    { title: "Embassy Level", desc: "Final attestation by the destination country's embassy." },
    { title: "Apostille", desc: "Certification for Hague Convention member countries." }
  ]

  return (
    <Layout>
      <SEO
        title="Document Verification - Academic Document Services | Welsh Education Group"
        description="Professional document verification and authentication services for international students."
        url="/services/documents"
      />

      <div className="service-page">
        <section className="service-hero-modern">
          <div className="container">
            <motion.div
              initial={{ opacity: 0, rotate: -10 }}
              animate={{ opacity: 1, rotate: 0 }}
              className="hero-icon-large"
            >
              <FileText size={40} />
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              Document Verification
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              Secure, fast, and reliable authentication for all your global academic and personal credentials.
            </motion.p>
          </div>
        </section>

        <section className="service-content-modern">
          <div className="container">
            <div className="service-grid-modern">
              <div className="main-service-info">
                <h2>End-to-End Attestation</h2>
                <p>
                  Navigating the complexities of international document verification can be daunting. We handle the entire process—from local verification to embassy attestation—ensuring your papers are legally recognized worldwide.
                </p>

                <div className="service-features-list">
                  {docTypes.map((type, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.1 }}
                      className="feature-pill"
                    >
                      <div className="feature-pill-icon">{type.icon}</div>
                      <div className="feature-pill-content">
                        <h4>{type.title}</h4>
                        <p>{type.desc}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>

                <div className="attestation-grid-section" style={{ marginTop: '64px' }}>
                  <h2 style={{ marginBottom: '32px' }}>Attestation Tiers</h2>
                  <div className="info-grid">
                    {attestationLevels.map((lvl, idx) => (
                      <div key={idx} className="info-card">
                        <h3>{lvl.title}</h3>
                        <p style={{ color: 'var(--muted-foreground)', fontSize: '14px' }}>{lvl.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="service-sidebar-modern">
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  className="sidebar-cta-card"
                >
                  <ShieldCheck className="text-gradient" size={48} style={{ margin: '0 auto 24px' }} />
                  <h3>Submit Documents</h3>
                  <p>Upload scans for a free initial authenticity check.</p>
                  <button className="sidebar-btn highlight-glow">Start Verification</button>
                </motion.div>

                <div className="info-card">
                  <h3 style={{ fontSize: '18px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Clock size={20} color="var(--purple-glow)" /> Timelines
                  </h3>
                  <div className="stats-list" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                      <span style={{ color: 'var(--muted-foreground)' }}>Educational</span>
                      <span>15-20 Days</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                      <span style={{ color: 'var(--muted-foreground)' }}>Personal</span>
                      <span>10-15 Days</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                      <span style={{ color: 'var(--muted-foreground)' }}>Apostille</span>
                      <span>7-10 Days</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  )
}

export default DocumentVerification
